import React from 'react';

import Badge from '@/components/Badge';
import TableRowButtons from '@/components/Common/TableRowButtons';
import { DEFAULT_EMPTY_VALUE } from '@/utils/constants';
import {
  createEnumOptions,
  getEnumTranslationKey,
  parseOptionalNumericEnumFromApi,
} from '@/utils/core/data-transformation/enumHelpers';
import { CoreEnums } from '@/utils/domain/constants';
import { createTableActionsColumn, getEnumNamespace } from '@/utils/infrastructure';
import { createHighlightRender } from '@/utils/infrastructure/highlightColumns';
import Filters, { registerModuleFilters } from '@/utils/input/filters';
import { formatCurrency } from '@/utils/output/formatters';

import { MODEL_NAME } from './constants';

const { BaseFilters } = Filters;

export const formatPlanAmount = (amount) =>
  formatCurrency(amount ?? 0, 'USD', {
    maximumFractionDigits: 2,
  });

const MONETARY_FIELDS = new Set([
  'monthlyFee',
  'monthlyOverageFee',
  'aiMonthlyIncludedCreditAmount',
]);

const BOOLEAN_DETAIL_FIELDS = new Set(['isAzureAdEnabled', 'isDigitalSignatureEnabled']);

const ScopeFilter = {
  ...BaseFilters.EnumFilter,
  name: 'scope',
  label: 'channelPlan.columns.scope',
  enumNamespace: getEnumNamespace(CoreEnums.AutomyPlanScope, 'Pricing.Plans'),
  enumSource: CoreEnums.AutomyPlanScope,
  options: createEnumOptions(CoreEnums.AutomyPlanScope, 'automyPlanScope', {
    casing: 'camel',
  }),
};

const KindFilter = {
  ...BaseFilters.EnumFilter,
  name: 'kind',
  label: 'channelPlan.columns.kind',
  enumNamespace: getEnumNamespace(CoreEnums.AutomyPlanKind, 'Pricing.Plans'),
  enumSource: CoreEnums.AutomyPlanKind,
  options: createEnumOptions(CoreEnums.AutomyPlanKind, 'automyPlanKind', {
    casing: 'camel',
  }),
};

const CategoryFilter = {
  ...BaseFilters.EnumFilter,
  name: 'category',
  label: 'channelPlan.columns.category',
  enumNamespace: getEnumNamespace(CoreEnums.AutomyPlanCategory, 'Pricing.Plans'),
  enumSource: CoreEnums.AutomyPlanCategory,
  filterSearch: true,
  options: createEnumOptions(CoreEnums.AutomyPlanCategory, 'automyPlanCategory', {
    casing: 'camel',
  }),
};

const TargetChannelActorKindFilter = {
  ...BaseFilters.EnumFilter,
  name: 'targetChannelActorKind',
  label: 'channelPlan.fields.targetChannelActorKind',
  enumNamespace: getEnumNamespace(CoreEnums.ChannelActorKind, 'Channel.Actors'),
  enumSource: CoreEnums.ChannelActorKind,
  filterSearch: true,
  options: createEnumOptions(CoreEnums.ChannelActorKind, 'channelActorKind', {
    casing: 'camel',
  }),
};

const ExclusiveCompanyFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'exclusiveCompany',
  label: 'channelPlan.fields.exclusiveCompany',
  labelInValue: true,
  displayField: 'commercialName',
  apiRoute: 'company/paged',
  filterSearch: true,
  params: {
    select: 'id,commercialName',
    filter: 'isActive eq true',
    orderBy: 'commercialName asc',
  },
  oDataField: 'exclusiveCompanyId',
};

const CreatedDateFilter = {
  ...Filters.DateFilter,
  name: 'created',
  label: 'global.created',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
};

const UpdatedDateFilter = {
  ...Filters.DateFilter,
  name: 'updated',
  label: 'global.updated',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
};

export const filtersConfig = [
  ScopeFilter,
  KindFilter,
  CategoryFilter,
  TargetChannelActorKindFilter,
  ExclusiveCompanyFilter,
  Filters.IsActiveFilter,
  CreatedDateFilter,
  UpdatedDateFilter,
];

registerModuleFilters(MODEL_NAME, filtersConfig);

/**
 * Domain detail mapping: list-matching badges for isActive / booleans, monetary formatting.
 * Other enum selects use `_form.js` locale options.
 */
export function mapChannelPlanDetailFields(rows, { item }) {
  if (!item || !rows?.length) return rows;

  return rows.map((row) => {
    if (row.name === 'isActive') {
      const isActive = item.isActive === true;
      return {
        ...row,
        type: 'badge',
        badgeType: 'group-item',
        badgeStatus: isActive ? 'success' : 'default',
        value: isActive ? 'global.text.enabled' : 'global.text.disabled',
        locale: true,
      };
    }

    if (BOOLEAN_DETAIL_FIELDS.has(row.name)) {
      const enabled = item[row.name] === true;
      return {
        ...row,
        type: 'badge',
        badgeType: 'group-item',
        badgeStatus: enabled ? 'success' : 'default',
        value: enabled ? 'global.text.yes' : 'global.text.no',
        locale: true,
      };
    }

    if (MONETARY_FIELDS.has(row.name)) {
      const value = item[row.name];
      if (value == null) {
        return { ...row, value: null };
      }
      return {
        ...row,
        value: formatPlanAmount(value),
      };
    }

    return row;
  });
}

const renderEnumBadge = (formatMessage, enumObject, value, translationPrefix) => {
  const resolved = parseOptionalNumericEnumFromApi(enumObject, value);
  if (resolved === undefined) {
    return <Badge status="default" type="process" text={DEFAULT_EMPTY_VALUE} />;
  }
  return (
    <Badge
      status="default"
      type="process"
      text={
        formatMessage({
          id: getEnumTranslationKey(enumObject, resolved, translationPrefix),
        }) || DEFAULT_EMPTY_VALUE
      }
    />
  );
};

export function buildChannelPlanColumns({ formatMessage, openDrawerCreateOrEdit, searchCriteria }) {
  const highlight = createHighlightRender(searchCriteria);

  return [
    {
      key: 'isActive',
      title: formatMessage({ id: 'global.text.enabled' }),
      dataIndex: 'isActive',
      align: 'center',
      sorter: true,
      width: 120,
      render: (value) => (
        <Badge
          status={value ? 'success' : 'default'}
          type="group-item"
          text={formatMessage({
            id: value ? 'global.text.enabled' : 'global.text.disabled',
          })}
        />
      ),
    },
    {
      key: 'name',
      title: formatMessage({ id: 'global.name' }),
      dataIndex: 'name',
      sorter: true,
      render: highlight,
    },
    {
      key: 'scope',
      title: formatMessage({ id: 'channelPlan.columns.scope' }),
      dataIndex: 'scope',
      align: 'center',
      sorter: true,
      render: (value) =>
        renderEnumBadge(formatMessage, CoreEnums.AutomyPlanScope, value, 'automyPlanScope'),
    },
    {
      key: 'kind',
      title: formatMessage({ id: 'channelPlan.columns.kind' }),
      dataIndex: 'kind',
      align: 'center',
      sorter: true,
      render: (value) =>
        renderEnumBadge(formatMessage, CoreEnums.AutomyPlanKind, value, 'automyPlanKind'),
    },
    {
      key: 'category',
      title: formatMessage({ id: 'channelPlan.columns.category' }),
      dataIndex: 'category',
      align: 'center',
      sorter: true,
      render: (value) =>
        renderEnumBadge(formatMessage, CoreEnums.AutomyPlanCategory, value, 'automyPlanCategory'),
    },
    createTableActionsColumn({
      inlineActionCount: 1,
      render: (record) => (
        <TableRowButtons
          onDetail={() => openDrawerCreateOrEdit(record.id, true)}
          formatMessage={formatMessage}
          actionOrder={{ primary: ['detail'], secondary: [] }}
        />
      ),
    }),
  ];
}
